import { getClientRuntimeConfig } from '@/config/runtime/clientRuntimeConfig';
import type { ContentType, IContentTags } from '@/interfaces';
import { useEffect, useRef } from 'react';

/**
 * Holds the current view's Sailthru context on the window object (tags by type.id),
 * ready for the tracker to be called at the right time. Will not send the URL twice.
 */
const saveTags = (id: string, type: ContentType, tags?: IContentTags) => {
    if (!(window as any).bnefSailthruTags) {
        (window as any).bnefSailthruTags = {};
    }
    (window as any).bnefSailthruTags[`${type}-${id}`] = tags;
};

const trackPageView = (urlPath: string, tags?: IContentTags) => {
    if (!('Sailthru' in window) || (window as any).bnefSailthruTracked?.includes(urlPath)) {
        return;
    }

    if (!('bnefSailthruInitialized' in window)) {
        try {
            (window as any).Sailthru.init({
                customerId: getClientRuntimeConfig().services.sailthruCustomerId,
                isCustom: true,
                autoTrackPageview: false,
                useStoredTags: false,
            });
            (window as any).bnefSailthruInitialized = true;
        } catch (e) {
            if (e instanceof URIError) {
                // Known Error: happens when url is /login?q=%88B
                console.info('Sailthru SPM initialization failure', e);
            } else {
                console.warn('Sailthru SPM initialization failure', e);
            }
            return;
        }
    }

    (window as any).Sailthru.track('pageview', {
        tags,
        url: urlPath,
    });
    if (!(window as any).bnefSailthruTracked) {
        (window as any).bnefSailthruTracked = [];
    }
    (window as any).bnefSailthruTracked.push(urlPath);
};

const trackSavedTags = (id: string, type: ContentType) => {
    const cacheKey = `${type}-${id}`;
    trackPageView((window as any).location.pathname, (window as any).bnefSailthruTags?.[cacheKey]);
};

export const SailthruTracking = { saveTags, trackPageView, trackSavedTags };

/**
 * For use on initial PageLoad
 */
export const usePageLoadSailthruTracking = (url: string, tags?: IContentTags) => {
    const previousUrlPath = useRef<string | null>(null);
    useEffect(() => {
        // remove query parameters from Sailthru tracking to ensure canonical URLs and single counting
        const urlPath = new URL(url, window.location.origin).pathname;
        // Skip tracking if URL hasn't changed
        if (previousUrlPath.current === urlPath) {
            previousUrlPath.current = urlPath;
            return;
        }

        previousUrlPath.current = urlPath;
        SailthruTracking.trackPageView(urlPath, tags);
    }, [url]);
};
